import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styles from "./BxgyBundle.module.css";
import BundlePreviewWrapper, {
    CustomSwatches,
    Swatches,
    VariantSelector,
    VariantSelectorV2,
} from "../../BundlePreviewWrapper/BundlePreviewWrapper";
import { ProductObj, BxgyConfigInterface, VariantsObj } from "../../Types";
import {
    getPriceLabel,
    getFinalPrice,
    getDiscountValueWithSign,
    getPixelsForImage,
    updateShowImage,
    AvailableBXGYTemplates,
    buildMetafieldFromSwatchOptions,
} from "../../constants";
import { TStoreContext } from "../../../../context/types";
import ProductImageWrapper from "../../BundlePreviewWrapper/ProductImageWrapper";
import useIsMobile from "../../../../hooks/useIsMobile";
import { hexToRgba } from "../../../utilities";
import PlusIconRound from "../../../../components/Icons/PlusIconRound";
import ProductSubscriptionsPreview from "../../BundlePreviewWrapper/ProductSubscriptionsPreview";
import { InspectorEditableBlock } from "../../CommonFields/InspectorEditableBlock";
import { flattenProducts, getProductId } from "../../../constants";
import { RenderVariantInfo } from "../../BundleBuilder/BundleBuilderBundle/BBProductCard";

const BxgyBundle = (bundleConfig: BxgyConfigInterface) => {
    return (
        !!bundleConfig && (
            <BxgyPreview
                bundleConfig={{
                    ...bundleConfig,
                    products: flattenProducts(bundleConfig.products, bundleConfig.should_flatten_products),
                    discounted_products: flattenProducts(
                        bundleConfig.discounted_products,
                        bundleConfig.should_flatten_products,
                    ),
                }}
                standAlone={true}
                appConfigContext={
                    {
                        currency: window.kefi_bundles.localization.currency,
                        money_format: window.kefi_bundles.money_format,
                    } as TStoreContext
                }
            />
        )
    );
};

type Props_BxgyPreview = {
    bundleConfig: BxgyConfigInterface;
    standAlone?: boolean;
    appConfigContext: TStoreContext;
};

const ProductCardShimmer = () => {
    return (
        <div
            style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "100px",
                fontSize: "20px",
                color: "#999999",
            }}
        >
            Select product(s)
        </div>
    );
};

const ProductCard = ({
    product,
    index,
    bundleConfig,
    appConfigContext,
    standAlone = false,
    setTrackVariant = () => {},
    discounted = false,
    unavailableSelected,
    setUnavailableSelected,
    isMobile,
    onSelectedVariantsChange,
    lockToVariants,
}: {
    product: ProductObj;
    index: number;
    bundleConfig: BxgyConfigInterface;
    appConfigContext: TStoreContext;
    standAlone: boolean;
    setTrackVariant: any;
    discounted?: boolean;
    unavailableSelected: number[];
    setUnavailableSelected: React.Dispatch<
        React.SetStateAction<{
            [key: number]: number[];
        }>
    >;
    isMobile: boolean;
    /** Reports this card's picked variants, used to drive "same variant as Buy item" on discounted cards. */
    onSelectedVariantsChange?: (productId: string, variants: VariantsObj[]) => void;
    /** Variants picked for this same product in the Buy list; when set, this card's variant is locked to them. */
    lockToVariants?: VariantsObj[];
}) => {
    const [disPrice, setDisPrice] = useState(0);
    const [origPrice, setOrigPrice] = useState(0);
    const [selectedVariants, setSelectedVariants] = useState<Array<VariantsObj>>(
        new Array(product.quantity)
            .fill(product.variants.find((vv) => vv.availableForSale == true) || product.variants[0])
            .map((variant) => ({
                ...variant,
                selling_plan: !product.oneTimePurchase
                    ? product.subscriptions && product?.subscriptions.find((sub) => sub.checked === true)?.id
                    : "",
                copyPrice: variant.price,
            })),
    );
    const { currency, money_format } = appConfigContext;
    // console._log("unavailableSelected", unavailableSelected);

    const is_horizontal = bundleConfig.selected_template_bxgy == AvailableBXGYTemplates.HORIZONTAL_BASIC;

    useEffect(() => {
        product.selectedVariants = selectedVariants;
        setTrackVariant((ps: any) => ps.map((item: any, idx: number) => (index === idx ? selectedVariants[0] : item)));
        // console._log("SELECTED_VARIANTS:", selectedVariants);

        let disP = selectedVariants.reduce((sum, item) => sum + parseFloat(item.price), 0);
        const origP = selectedVariants.reduce((sum, item) => sum + parseFloat(item.price), 0);
        const dis = bundleConfig?.discount_value || 0;
        switch (bundleConfig?.discount_type) {
            case "percentage":
                disP = disP - disP * (dis / 100);
                break;
            case "flat":
                if (bundleConfig.discounted_products.length == 1) {
                    disP =
                        disP -
                        (dis /
                            bundleConfig.discounted_products.reduce((acc, c) => {
                                acc += c.quantity;
                                return acc;
                            }, 0)) *
                            product.quantity;
                }
                break;
            case "fixed":
                if (bundleConfig.discounted_products.length == 1) {
                    disP =
                        (dis /
                            bundleConfig.discounted_products.reduce((acc, c) => {
                                acc += c.quantity;
                                return acc;
                            }, 0)) *
                        product.quantity;
                }
                break;
            default:
        }
        if (discounted) {
            setDisPrice(Math.round(disP * 100) / 100);
        } else {
            setDisPrice(Math.round(origP * 100) / 100);
        }
        setOrigPrice(Math.round(origP * 100) / 100);
    }, [
        selectedVariants,
        product,
        discounted,
        bundleConfig?.discount_type,
        bundleConfig?.discount_value,
        setTrackVariant,
        index,
    ]);

    useEffect(() => {
        setSelectedVariants(
            new Array(product.quantity)
                .fill(product.variants.find((vv) => vv.availableForSale == true) || product.variants[0])
                .map((variant) => ({
                    ...variant,
                    selling_plan: !product.oneTimePurchase
                        ? product.subscriptions && product?.subscriptions.find((sub) => sub.checked === true)?.id
                        : "",
                    copyPrice: variant.price,
                })),
        );
    }, [product, bundleConfig]);

    // only lock when every Buy unit's variant is also offered on this discounted product
    const lockedVariants = useMemo(() => {
        if (!lockToVariants?.length) return null;
        const matched = lockToVariants.map((lv) => product.variants.find((v) => v.id === lv.id));
        return matched.every(Boolean) ? (matched as VariantsObj[]) : null;
    }, [lockToVariants, product]);
    const lockedVariantsKey = lockedVariants?.map((v) => v.id).join(",") ?? "";

    useEffect(() => {
        if (!lockedVariants) return;
        setSelectedVariants(
            new Array(product.quantity).fill(null).map((_, i) => {
                const variant = lockedVariants[Math.min(i, lockedVariants.length - 1)];
                return {
                    ...variant,
                    selling_plan: !product.oneTimePurchase
                        ? product.subscriptions && product?.subscriptions.find((sub) => sub.checked === true)?.id
                        : "",
                    copyPrice: variant.price,
                };
            }),
        );
    }, [lockedVariantsKey, product, bundleConfig]);

    useEffect(() => {
        onSelectedVariantsChange?.(product.id, selectedVariants);
    }, [selectedVariants, product.id, onSelectedVariantsChange]);

    useEffect(() => {
        // Add productIdx to each selectedVariant
        const updatedSelectedVariants = selectedVariants.map((variant: any) => ({
            ...variant,
            productIdx: index, // Add the product index
        }));

        product.selectedVariants = updatedSelectedVariants; // Assign updated variants to the product

        // console._log("updatedSelectedVariants", updatedSelectedVariants);

        // Update trackVariant state
        setTrackVariant((prevVariants: any) => {
            const updatedVariants = [...prevVariants];
            updatedVariants[index] = updatedSelectedVariants; // Update the specific index with updated variants
            return updatedVariants;
        });

        // let totalOriginalPrice = 0;
        // let totalDiscountedPrice = 0;

        // Calculate total original price
        // updatedSelectedVariants.forEach((variant: any) => {
        // const variantPrice = +variant?.price || 0;
        // totalOriginalPrice += variantPrice;
        // });

        // Apply discount based on bundleConfig after summing the prices
        // totalDiscountedPrice = totalOriginalPrice;
        // const discountValue = bundleConfig?.discount_value || 0;

        // switch (bundleConfig?.discount_type) {
        //     case "percentage":
        //         totalDiscountedPrice =
        //             totalOriginalPrice -
        //             totalOriginalPrice * (discountValue / 100);
        //         break;
        //     default:
        //         break;
        // }

        // // Set the final calculated prices, rounded to 2 decimal places
        // if (discounted) {
        //     setDisPrice(Math.round(totalDiscountedPrice * 100) / 100);
        // } else {
        //     setDisPrice(Math.round(totalOriginalPrice * 100) / 100);
        // }
        // setOrigPrice(Math.round(totalOriginalPrice * 100) / 100);
    }, [selectedVariants, product, bundleConfig, index]);

    const handleProductClick = (e: any) => {
        e?.preventDefault();
        if (standAlone && !window.location.pathname.includes(product.handle)) {
            window.open(`https://${window?.location?.host}/products/${product.handle}`, "_blank");
        } else if (!window.location.pathname.includes(product.handle)) {
            window.open(`https://${shopify?.config?.shop}/products/${product.handle}`, "_blank");
        }
    };

    useEffect(() => {
        if (product.subscriptions && product.subscriptions.length > 0 && !product.oneTimePurchase) {
            product.isSubscribed = true;
        }
    }, [product, product.oneTimePurchase]);

    const renderPrice = (
        <div
            style={{
                color: bundleConfig?.bundles_design.highlighted_price_tag_color,
                fontSize: bundleConfig?.bundles_design.highlighted_price_tag_size,
                fontWeight: bundleConfig?.bundles_design.highlighted_price_tag_style?.split(" ")[0],
                fontStyle: bundleConfig?.bundles_design.highlighted_price_tag_style?.split(" ")[1],
                display: "flex",
                flexDirection: is_horizontal ? "row" : isMobile ? "row" : "column",
                justifyContent: "flex-end",
                alignItems: is_horizontal ? "baseline" : isMobile ? "center" : "flex-end",
                gap: is_horizontal ? "2px" : isMobile ? "2px" : "0px",
                position: "relative",
            }}
        >
            <InspectorEditableBlock bundleType={bundleConfig?.bundle_type} settingKey="productPrice">
                <div
                    style={{
                        width: "max-content",
                    }}
                >
                    {getFinalPrice("none", 0, disPrice) <= 0
                        ? bundleConfig.free_text || "Free"
                        : getPriceLabel(getFinalPrice("none", 0, disPrice), money_format, currency)}
                </div>
            </InspectorEditableBlock>

            {discounted &&
                (bundleConfig.discounted_products.length == 1 ||
                    (bundleConfig.use_compare_at_price &&
                        (bundleConfig.discount_type === "fixed" || bundleConfig.discount_type === "flat")) ||
                    bundleConfig.discount_type === "percentage") && (
                    <InspectorEditableBlock bundleType={bundleConfig?.bundle_type} settingKey="productCompareAtPrice">
                        <div
                            style={{
                                textDecoration: "line-through",
                                fontSize: bundleConfig?.bundles_design.product_price_size,
                                color: bundleConfig?.bundles_design?.product_price_color,
                                fontWeight: bundleConfig?.bundles_design?.product_price_style?.split(" ")[0],
                                fontStyle: bundleConfig?.bundles_design?.product_price_style?.split(" ")[1],
                                width: "max-content",
                                lineHeight: "normal",
                            }}
                        >
                            {getFinalPrice("none", 0, disPrice) <
                                getFinalPrice(
                                    "none",
                                    0,
                                    bundleConfig.use_compare_at_price
                                        ? selectedVariants.reduce((total, sv) => total + +sv.compareAtPrice, 0)
                                        : origPrice,
                                ) &&
                                getPriceLabel(
                                    getFinalPrice(
                                        "none",
                                        0,
                                        bundleConfig.use_compare_at_price
                                            ? selectedVariants.reduce((total, sv) => total + +sv.compareAtPrice, 0)
                                            : origPrice,
                                    ),
                                    money_format,
                                    currency,
                                )}
                        </div>
                    </InspectorEditableBlock>
                )}
        </div>
    );

    const userShopname = standAlone ? window.kefi_bundles.shop : shopify.config.shop;
    const effectiveMetafield = buildMetafieldFromSwatchOptions({
        product,
        bundleConfig: bundleConfig || {},
    });

    const isProductAvailable = useMemo(() => {
        return product.variants.some((vv) => (vv.quantityAvailable && vv.quantityAvailable > 0) || vv.availableForSale);
    }, [product]);

    isMobile = is_horizontal ? false : isMobile;

    const productImageWrapperUI = (
        <>
            <ProductImageWrapper
                product={product}
                handleProductClick={handleProductClick}
                bundleConfig={
                    is_horizontal
                        ? {
                              ...bundleConfig,
                              bundles_design: {
                                  ...bundleConfig?.bundles_design,
                                  product_img_size: bundleConfig?.bundles_design.product_img_size
                                      ? "fbt_" + bundleConfig?.bundles_design.product_img_size
                                      : "fbt_small",
                              },
                          }
                        : bundleConfig
                }
                isMobile={isMobile}
                selectedVariants={selectedVariants}
            />
        </>
    );

    const variantTextStyle = {
        color: bundleConfig?.bundles_design?.variant_selector_text_color,
        fontSize: bundleConfig?.bundles_design?.variant_selector_text_size,
        fontWeight: bundleConfig?.bundles_design?.variant_selector_text_style?.split(" ")[0] ?? "450",
        fontStyle: bundleConfig?.bundles_design?.variant_selector_text_style?.split(" ")[1] ?? "normal",
    };

    const isTypsyStore = userShopname === "typsy-beauty.myshopify.com";
    const usesSwatches = isTypsyStore || (bundleConfig?.show_swatches && effectiveMetafield);
    // only the locked variant(s) are passed in, so each option shows just the chosen swatch
    const lockedSwatchVariants = selectedVariants.filter(
        (v, i, arr) => arr.findIndex((other) => other.id === v.id) === i,
    );

    const lockedVariantUI = (
        <div className={styles.lockedVariantWrapper}>
            {selectedVariants.map((variant, i) =>
                usesSwatches ? (
                    <div className={styles.lockedSwatches} key={i}>
                        {isTypsyStore ? (
                            <Swatches
                                bundlesDesign={bundleConfig?.bundles_design}
                                variants={lockedSwatchVariants}
                                handleChange={() => {}}
                                selected={selectedVariants}
                                options={product.options || []}
                                productCardIndex={i}
                                standAlone={standAlone}
                            />
                        ) : (
                            <CustomSwatches
                                variants={lockedSwatchVariants}
                                handleChange={() => {}}
                                selected={selectedVariants}
                                options={product.options || []}
                                productCardIndex={i}
                                standAlone={standAlone}
                                metafield={effectiveMetafield}
                                bundlesDesign={bundleConfig?.bundles_design}
                                bundleConfig={bundleConfig}
                            />
                        )}
                    </div>
                ) : (
                    <div className={styles.lockedOptionsRow} key={i}>
                        {(variant.selectedOptions || [])
                            // skip options with a single value across the product's variants, like the dropdowns do
                            .filter((option: any) =>
                                product.variants.some((v) =>
                                    v.selectedOptions?.some(
                                        (o: any) => o.name === option.name && o.value !== option.value,
                                    ),
                                ),
                            )
                            .map((option: any) => (
                                <div className={styles.lockedOption} key={option.name}>
                                    <span style={variantTextStyle}>{option.name}</span>
                                    <div className={styles.lockedValue} style={variantTextStyle}>
                                        <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                                            <path d="M17 9h-1V7a4 4 0 0 0-8 0v2H7a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-9a2 2 0 0 0-2-2Zm-7-2a2 2 0 1 1 4 0v2h-4V7Z" />
                                        </svg>
                                        <span className={styles.lockedValueText}>{option.value}</span>
                                    </div>
                                </div>
                            ))}
                    </div>
                ),
            )}
        </div>
    );

    // a single variant picked out of a multi-variant product has no dropdown, so show its options as text
    const showSingleVariantInfo =
        bundleConfig.bundle_product_level === "product" &&
        (product?.variantsCount?.count ?? 0) > 1 &&
        product.variants.length == 1;
    const singleVariantName =
        product.variants[0]?.title?.toLowerCase() != product?.title?.toLowerCase() ? product.variants[0]?.title : "";

    const singleVariantInfoUI = (
        <div style={{ marginTop: "6px", width: "100%" }}>
            <RenderVariantInfo
                product={singleVariantName ? { ...product, title: singleVariantName } : product}
                style={variantTextStyle}
                bundleType={bundleConfig?.bundle_type}
            />
        </div>
    );

    const variantSelectorUI = (
        <>
            {showSingleVariantInfo
                ? null
                : lockedVariants && product.variants.length > 1
                  ? lockedVariantUI
                  : product.variants.length > 1 && (
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "5px",
                                width: "100%",
                                marginTop: "10px",
                            }}
                        >
                            {selectedVariants.map((_: any, i: number) =>
                                userShopname === "typsy-beauty.myshopify.com" ? (
                                    <Swatches
                                        bundlesDesign={bundleConfig?.bundles_design}
                                        key={i}
                                        variants={product.variants}
                                        handleChange={setSelectedVariants}
                                        selected={selectedVariants}
                                        options={product.options || []}
                                        productCardIndex={i}
                                        standAlone={standAlone}
                                    />
                                ) : bundleConfig?.show_swatches && effectiveMetafield ? (
                                    <CustomSwatches
                                        key={i}
                                        variants={product.variants}
                                        handleChange={setSelectedVariants}
                                        selected={selectedVariants}
                                        options={product.options || []}
                                        productCardIndex={i}
                                        standAlone={standAlone}
                                        metafield={effectiveMetafield}
                                        bundlesDesign={bundleConfig?.bundles_design}
                                        bundleConfig={bundleConfig}
                                    />
                                ) : (
                                    <VariantSelectorV2
                                        key={i}
                                        variants={product.variants}
                                        handleChange={setSelectedVariants}
                                        selected={selectedVariants}
                                        options={product.options || []}
                                        productCardIndex={i}
                                        standAlone={standAlone}
                                        metafield={effectiveMetafield}
                                        bundlesDesign={bundleConfig?.bundles_design}
                                        bundleConfig={bundleConfig}
                                    />
                                ),
                            )}
                        </div>
                    )}
        </>
    );

    const subInfoUI = (
        <>
            <div className={styles.subInfo}>
                {unavailableSelected.length > 0 ? (
                    <>
                        <div
                            style={{
                                color: "#cc3e3e",
                                fontWeight: "bold",
                                marginBottom: "10px",
                                fontSize: "10px",
                                textAlign: "right",
                            }}
                        >
                            One or more variants are not available
                        </div>
                    </>
                ) : (
                    <>
                        {isMobile &&
                            (isProductAvailable ? (
                                renderPrice
                            ) : (
                                <span
                                    style={{
                                        fontSize: "12px",
                                        color: "red",
                                    }}
                                >
                                    {bundleConfig?.bundles_settings?.out_of_stock_text}
                                </span>
                            ))}
                    </>
                )}
            </div>
        </>
    );

    const subscriptionSelectorUI = (
        <>
            <ProductSubscriptionsPreview
                product={product}
                setSelectedVariants={setSelectedVariants}
                key={product.id}
                bundleConfig={bundleConfig}
            />
        </>
    );

    return is_horizontal ? (
        <>
            <InspectorEditableBlock
                bundleType={bundleConfig?.bundle_type}
                settingKey="productCard"
                tagsKey={product?.tags?.toString()}
                productId={getProductId(product?.id)}
            >
                <div
                    className={`${styles.productCard} ${styles.horizontal}`}
                    style={{
                        alignItems: "center",
                    }}
                >
                    {productImageWrapperUI}

                    <div
                        style={{
                            color: bundleConfig?.bundles_design.highlighted_price_tag_color,
                            fontSize: bundleConfig?.bundles_design.highlighted_price_tag_size,
                            fontWeight: bundleConfig?.bundles_design.highlighted_price_tag_style?.split(" ")[0],
                            fontStyle: bundleConfig?.bundles_design.highlighted_price_tag_style?.split(" ")[1],
                            gap: "0px",
                            marginTop: 10,
                        }}
                        className={styles.productInfo}
                    >
                        <InspectorEditableBlock bundleType={bundleConfig?.bundle_type} settingKey="productTitle">
                            <div
                                style={{
                                    fontSize: bundleConfig?.bundles_design.product_name_size,
                                    color: bundleConfig?.bundles_design?.product_name_color,
                                    fontWeight: bundleConfig?.bundles_design?.product_name_style?.split(" ")[0],
                                    fontStyle: bundleConfig?.bundles_design?.product_name_style?.split(" ")[1],
                                    cursor: "pointer",
                                    width: "100%",
                                }}
                                onClick={handleProductClick}
                            >
                                {product?.title}
                            </div>
                        </InspectorEditableBlock>
                        {bundleConfig?.show_product_quantity !== false && (
                            <InspectorEditableBlock bundleType={bundleConfig?.bundle_type} settingKey="productQuantity">
                                <div
                                    className={styles.qtyValue}
                                    style={{ color: bundleConfig?.bundles_design?.product_name_color }}
                                >
                                    {bundleConfig?.quantity_text || "Qty"} : {product.quantity || 0}
                                </div>
                            </InspectorEditableBlock>
                        )}
                        {showSingleVariantInfo && singleVariantInfoUI}
                        <div className={styles.subInfo}>
                            {unavailableSelected.length > 0 ? (
                                <>
                                    <div
                                        style={{
                                            color: "#cc3e3e",
                                            fontWeight: "bold",
                                            marginBottom: "10px",
                                            fontSize: "10px",
                                            textAlign: "right",
                                        }}
                                    >
                                        One or more variants are not available
                                    </div>
                                </>
                            ) : (
                                <>
                                    {isProductAvailable ? (
                                        renderPrice
                                    ) : (
                                        <span
                                            style={{
                                                fontSize: "12px",
                                                color: "red",
                                            }}
                                        >
                                            {bundleConfig?.bundles_settings?.out_of_stock_text}
                                        </span>
                                    )}
                                </>
                            )}
                        </div>
                    </div>

                    {variantSelectorUI}

                    {/* {product.isSubscribed && (
                        <div style={{ width: "100%" }}>
                            <div style={{ margin: "5px 0" }}>
                                <span className={styles.separator}></span>
                            </div>
                            {subscriptionSelectorUI}
                        </div>
                    )} */}
                </div>
            </InspectorEditableBlock>
        </>
    ) : (
        <InspectorEditableBlock
            bundleType={bundleConfig?.bundle_type}
            settingKey="productCard"
            tagsKey={product?.tags?.toString()}
            productId={getProductId(product?.id)}
        >
            <div className={`${styles.productCard}`}>
                <div className={styles.productInfoRight}>
                    {productImageWrapperUI}
                    <div
                        className={styles.productMetaRight}
                        style={{
                            color: bundleConfig?.bundles_design.highlighted_price_tag_color,
                            fontSize: bundleConfig?.bundles_design.highlighted_price_tag_size,
                            fontWeight: bundleConfig?.bundles_design.highlighted_price_tag_style?.split(" ")[0],
                            fontStyle: bundleConfig?.bundles_design.highlighted_price_tag_style?.split(" ")[1],
                            gap: isMobile
                                ? +getPixelsForImage(bundleConfig?.bundles_design?.product_img_size, "number") / 10 +
                                  "px"
                                : "0px",
                        }}
                    >
                        <div
                            style={{
                                fontSize: bundleConfig?.bundles_design.product_name_size,
                                color: bundleConfig?.bundles_design?.product_name_color,
                                fontWeight: bundleConfig?.bundles_design?.product_name_style?.split(" ")[0],
                                fontStyle: bundleConfig?.bundles_design?.product_name_style?.split(" ")[1],
                                cursor: "pointer",
                                width: "100%",
                                display: "flex",
                                alignItems: "flex-start",
                                justifyContent: "space-between",
                            }}
                            onClick={handleProductClick}
                        >
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: isMobile ? "row" : "column",
                                    alignItems: isMobile ? "center" : "flex-start",
                                    justifyContent: isMobile ? "space-between" : "flex-start",
                                    gap: isMobile ? 4 : 8,
                                }}
                            >
                                <InspectorEditableBlock
                                    bundleType={bundleConfig?.bundle_type}
                                    settingKey="productTitle"
                                    wrapWithDiv={true}
                                >
                                    <span> {product?.title}</span>
                                </InspectorEditableBlock>
                                {(!isMobile ||
                                    !updateShowImage(bundleConfig?.bundles_design?.product_img_show, isMobile)) && (
                                    <div
                                        style={{
                                            display: "flex",
                                            gap: "10px",
                                            alignItems: "center",
                                        }}
                                    >
                                        {bundleConfig?.show_product_quantity !== false && (
                                            <InspectorEditableBlock
                                                bundleType={bundleConfig?.bundle_type}
                                                settingKey="productQuantity"
                                            >
                                                <div
                                                    className={styles.qtyValue}
                                                    style={{ color: bundleConfig?.bundles_design?.product_name_color }}
                                                >
                                                    {bundleConfig.quantity_text || "Qty"} : {product.quantity || 0}
                                                </div>
                                            </InspectorEditableBlock>
                                        )}
                                        {/* {subscriptionSelectorUI} */}
                                    </div>
                                )}
                            </div>

                            {unavailableSelected.length == 0 &&
                                (isProductAvailable ? (
                                    renderPrice
                                ) : (
                                    <span
                                        style={{
                                            fontSize: "12px",
                                            color: "red",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {bundleConfig?.bundles_settings?.out_of_stock_text}
                                    </span>
                                ))}
                        </div>
                        {showSingleVariantInfo && singleVariantInfoUI}
                        {/* {subInfoUI} */}
                    </div>
                </div>
                {variantSelectorUI}

                {/* {product.isSubscribed && (
                    <div style={{ width: "100%" }}>
                        <div style={{ margin: "5px 0" }}>
                            <span className={styles.separator}></span>
                        </div>

                        {subscriptionSelectorUI}
                    </div>
                )} */}
            </div>
        </InspectorEditableBlock>
    );
};

export const BxgyPreview = ({ bundleConfig, standAlone, appConfigContext }: Props_BxgyPreview) => {
    const [totalPrice, setTotalPrice] = useState(0);
    const [compareAtPrice, setCompareAtPrice] = useState(0);
    const [discountedPrice, setDiscountedPrice] = useState(0);
    const { currency, money_format } = appConfigContext;
    const [trackVariant, setTrackVariant] = useState<any[]>([]);
    const bundleRef = useRef(null);
    const { isMobile } = useIsMobile();
    const [trackDiscountedVariant, setTrackDiscountedVariants] = useState<any[]>([]);
    const [unavailableSelected, setUnavailableSelected] = useState<{
        [key: number]: number[];
    }>({});
    const [unavailableDiscountSelected, setUnavailableDiscountSelected] = useState<{
        [key: number]: number[];
    }>({});

    const is_horizontal = bundleConfig.selected_template_bxgy == AvailableBXGYTemplates.HORIZONTAL_BASIC;

    // variants picked on Buy cards, keyed by product id; drives "same variant as Buy item" on discounted cards
    const [buyVariantsByProduct, setBuyVariantsByProduct] = useState<Record<string, VariantsObj[]>>({});
    const handleBuyVariantsChange = useCallback((productId: string, variants: VariantsObj[]) => {
        setBuyVariantsByProduct((ps) => (ps[productId] === variants ? ps : { ...ps, [productId]: variants }));
    }, []);
    const isSameVariantAsBuy = bundleConfig?.discounted_variant_mode === "same_as_buy";

    const combinedVariants = [...trackVariant, ...trackDiscountedVariant];
    const transformedVariants = Array.isArray(combinedVariants)
        ? combinedVariants
              .map((variants) => {
                  return Array.isArray(variants)
                      ? variants.map((variant: any) => {
                            return variant.id.split("/").pop();
                        })
                      : []; // Return empty array if variants is not an array
              })
              .flat()
        : []; // Return empty array if trackVariant is not an array

    useEffect(() => {
        if (trackVariant?.length && trackDiscountedVariant?.length) {
            let totalPrice = 0;
            let disPrice = 0;
            let compPrice = 0;

            // if (trackVariant.every((item) => Array.isArray(item))) {
            //     setTrackVariant(trackVariant.flatMap((a) => a));
            // }
            // if (trackDiscountedVariant.every((item) => Array.isArray(item))) {
            //     setTrackDiscountedVariants(
            //         trackDiscountedVariant.flatMap((a) => a),
            //     );
            // }
            trackVariant.forEach((productVariants) => {
                if (productVariants && productVariants.length) {
                    productVariants.forEach((variant: any) => {
                        if (variant && variant.price) {
                            totalPrice += +variant.price; // Add each variant's price to the total
                        }
                        if (variant && variant.compareAtPrice) {
                            compPrice += +variant.price;
                        }
                    });
                }
            });
            trackDiscountedVariant.forEach((productVariants) => {
                if (productVariants && productVariants.length) {
                    productVariants.forEach((variant: any) => {
                        if (variant && variant.price) {
                            disPrice += +variant.price; // Add each variant's price to the total
                        }
                        if (variant && variant.compareAtPrice) {
                            compPrice += +variant.compareAtPrice;
                        }
                    });
                }
            });

            setDiscountedPrice(isNaN(disPrice) ? 0 : disPrice);
            setTotalPrice(isNaN(totalPrice) ? 0 : totalPrice);
            setCompareAtPrice(isNaN(compPrice) ? 0 : compPrice);
        } else {
            setDiscountedPrice(0);
            setTotalPrice(0);
        }
    }, [
        trackVariant,
        trackDiscountedVariant,
        bundleConfig?.products,
        bundleConfig?.discounted_products,
        bundleConfig?.discount_type,
        bundleConfig?.discount_value,
    ]);

    const isAnyProductUnavailable = useMemo(() => {
        let flag = false;
        for (let i = 0; i < bundleConfig.products.length; i++) {
            if (flag) break;
            const product = bundleConfig.products[i];
            const { variants } = product;

            const availableVariantsCount = variants.filter(
                (variant: any) => variant.quantityAvailable > product.quantity,
            ).length;

            if (availableVariantsCount == 0) {
                flag = true;
            }
        }

        for (let i = 0; i < bundleConfig.discounted_products.length; i++) {
            if (flag) break;
            const product = bundleConfig.discounted_products[i];
            const { variants } = product;

            const availableVariantsCount = variants.filter(
                (variant: any) => variant.quantityAvailable > product.quantity,
            ).length;

            if (availableVariantsCount == 0) {
                flag = true;
            }
        }

        return flag;
    }, [bundleConfig.products.length]);

    useEffect(() => {
        const temp: any[] = [];
        const temp2: any[] = [];

        bundleConfig?.products.forEach((pd) => {
            if (pd.selectedVariants?.length > 0) {
                temp.push(pd.selectedVariants[0]);
            }
        });
        bundleConfig?.discounted_products.forEach((pd) => {
            if (pd.selectedVariants?.length > 0) {
                temp2.push(pd.selectedVariants[0]);
            }
        });

        setTrackDiscountedVariants(temp2);
        setTrackVariant(temp);
    }, [bundleConfig?.products, bundleConfig?.discounted_products]);

    const separatorUI = is_horizontal ? (
        <div className={styles.plusSign} style={{ maxWidth: "20px" }}>
            <PlusIconRound color={bundleConfig?.bundles_design?.special_text_background_color} />
        </div>
    ) : (
        <div
            className={styles.separator}
            style={{
                borderBottom: `1px solid lightgray`,
            }}
        ></div>
    );

    if (
        !bundleConfig ||
        //@ts-expect-error hide_on_out_of_stock for a user
        (standAlone && bundleConfig?.bundles_settings?.hide_on_out_of_stock && isAnyProductUnavailable)
    )
        return null;

    const BundlePreviewWrapperUI = (
        <BundlePreviewWrapper
            bundleConfig={bundleConfig}
            standAlone={standAlone}
            unavailableDiscountSelected={unavailableDiscountSelected}
            transformedVariants={transformedVariants}
            currency={currency}
            money_format={money_format}
            totalPrice={totalPrice}
            discountedPrice={discountedPrice}
            compareAtPriceTotal={compareAtPrice}
        >
            <div
                style={
                    is_horizontal
                        ? {
                              flexDirection: "row",
                              ...(standAlone && isMobile ? { overflow: "auto", maxWidth: "92vw" } : {}),
                          }
                        : {}
                }
                className={styles.products}
                ref={bundleRef}
            >
                {bundleConfig?.products?.length && bundleConfig?.discounted_products.length ? (
                    <>
                        <InspectorEditableBlock bundleType={bundleConfig?.bundle_type} settingKey="productCard">
                            <span style={{ width: "inherit", display: "inline-block" }}>
                                <div
                                    className={`${styles.subProductWrapper} ${styles.youBuy} ${
                                        is_horizontal ? styles.horizontal : ""
                                    }`}
                                    style={{
                                        padding: is_horizontal ? undefined : "18px 16px 16px",
                                        border: `${bundleConfig?.bundles_design?.border_width}px solid ${bundleConfig?.bundles_design?.product_border_color}`,
                                        borderRadius: bundleConfig?.bundles_design?.border_radius,
                                        boxShadow:
                                            bundleConfig.selected_template_bxgy ==
                                            AvailableBXGYTemplates.HORIZONTAL_BASIC
                                                ? "none"
                                                : `0px 2px 9px 0px ${hexToRgba(
                                                      bundleConfig?.bundles_design?.product_border_color,
                                                      0.5,
                                                  )}`,
                                        backgroundColor: bundleConfig?.bundles_design?.product_background_color,
                                        lineHeight: "normal",
                                        paddingTop: is_horizontal
                                            ? undefined
                                            : isMobile
                                              ? bundleConfig.bundles_design.special_text_font_size + 14 + "px"
                                              : bundleConfig.bundles_design.special_text_font_size + 8 + "px",
                                        margin: "12px 2px",
                                        height: "94%",
                                    }}
                                >
                                    <div
                                        className={styles.youBuyLabelWrapper}
                                        style={{
                                            background: "red",
                                            fontWeight: bundleConfig?.bundles_design.special_text_style?.split(" ")[0],
                                            fontStyle: bundleConfig?.bundles_design.special_text_style?.split(" ")[1],
                                            fontSize: bundleConfig.bundles_design.special_text_font_size,
                                            color: bundleConfig?.bundles_design.special_text_color,
                                            borderRadius: 8,
                                            border: `${bundleConfig?.bundles_design?.border_width}px solid ${bundleConfig?.bundles_design?.product_border_color}`,
                                            backgroundColor:
                                                bundleConfig?.bundles_design?.special_text_background_color,
                                        }}
                                    >
                                        <InspectorEditableBlock
                                            bundleType={bundleConfig?.bundle_type}
                                            settingKey="discountTag"
                                        >
                                            <div
                                                style={{
                                                    width: "inherit",
                                                }}
                                            >
                                                {bundleConfig?.x_label_buy_text || "BUY"}
                                            </div>
                                        </InspectorEditableBlock>
                                    </div>{" "}
                                    {bundleConfig?.products?.map((product, index) => {
                                        return (
                                            <>
                                                <ProductCard
                                                    discounted={false}
                                                    key={"1" + product?.id + product?.variants[0]?.id}
                                                    index={index}
                                                    product={product}
                                                    bundleConfig={bundleConfig}
                                                    appConfigContext={appConfigContext}
                                                    standAlone={standAlone || false}
                                                    setTrackVariant={setTrackVariant}
                                                    setUnavailableSelected={setUnavailableSelected}
                                                    unavailableSelected={unavailableSelected[index] || []}
                                                    isMobile={isMobile}
                                                    onSelectedVariantsChange={
                                                        // the first Buy card of a product is the one to match
                                                        bundleConfig.products.findIndex((p) => p.id === product.id) ===
                                                        index
                                                            ? handleBuyVariantsChange
                                                            : undefined
                                                    }
                                                />
                                                {index < bundleConfig?.products?.length - 1 && separatorUI}
                                            </>
                                        );
                                    })}
                                </div>
                            </span>
                        </InspectorEditableBlock>
                        <InspectorEditableBlock bundleType={bundleConfig?.bundle_type} settingKey="plusSign">
                            <div className={styles.plusSign} style={{ maxWidth: is_horizontal ? "20px" : undefined }}>
                                <PlusIconRound
                                    size={25}
                                    color={bundleConfig?.bundles_design?.special_text_background_color}
                                />
                            </div>
                        </InspectorEditableBlock>

                        <InspectorEditableBlock bundleType={bundleConfig?.bundle_type} settingKey="productCard">
                            <div
                                style={{
                                    width: "inherit",
                                    display: "inline-block",
                                }}
                            >
                                <div
                                    className={`${styles.subProductWrapper} ${styles.youGet}  ${
                                        is_horizontal ? styles.horizontal : ""
                                    }`}
                                    style={{
                                        padding: is_horizontal ? undefined : "16px 16px",
                                        border: `${bundleConfig?.bundles_design?.border_width}px solid ${bundleConfig?.bundles_design?.product_border_color}`,
                                        borderRadius: bundleConfig?.bundles_design?.border_radius,
                                        boxShadow:
                                            bundleConfig.selected_template_bxgy ==
                                            AvailableBXGYTemplates.HORIZONTAL_BASIC
                                                ? "none"
                                                : `0px 2px 9px 0px ${hexToRgba(
                                                      bundleConfig?.bundles_design?.product_border_color,
                                                      0.5,
                                                  )}`,
                                        backgroundColor: bundleConfig?.bundles_design.product_background_color,
                                        paddingTop: is_horizontal
                                            ? undefined
                                            : isMobile
                                              ? bundleConfig.bundles_design.special_text_font_size + 14 + "px"
                                              : bundleConfig.bundles_design.special_text_font_size + 8 + "px",
                                        margin: "12px 2px",
                                        height: "94%",
                                    }}
                                >
                                    {bundleConfig?.discount_type !== "none" &&
                                        !(
                                            bundleConfig?.discount_type == "percentage" &&
                                            bundleConfig?.discount_value == 0
                                        ) &&
                                        !(
                                            bundleConfig?.discount_type == "flat" && bundleConfig?.discount_value == 0
                                        ) && (
                                            <InspectorEditableBlock
                                                bundleType={bundleConfig?.bundle_type}
                                                settingKey="discountTag"
                                            >
                                                <div
                                                    className={styles.youGetLabelWrapper}
                                                    style={{
                                                        color: bundleConfig?.bundles_design.special_text_color,
                                                        fontWeight:
                                                            bundleConfig?.bundles_design.special_text_style?.split(
                                                                " ",
                                                            )[0],
                                                        fontStyle:
                                                            bundleConfig?.bundles_design.special_text_style?.split(
                                                                " ",
                                                            )[1],
                                                        fontSize: bundleConfig.bundles_design.special_text_font_size,
                                                        backgroundColor:
                                                            bundleConfig?.bundles_design.special_text_background_color,
                                                        borderRadius: 8,
                                                        visibility:
                                                            bundleConfig.bundles_design.special_text_font_size == 0
                                                                ? "hidden"
                                                                : "visible",
                                                        padding: 2,
                                                        paddingRight: 7,
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "flex-start",
                                                        gap: 4,
                                                        border: `${bundleConfig?.bundles_design?.border_width}px solid ${bundleConfig?.bundles_design?.product_border_color}`,
                                                    }}
                                                >
                                                    <span
                                                        style={{
                                                            fontSize:
                                                                bundleConfig.bundles_design.special_text_font_size +
                                                                1 +
                                                                "px",
                                                            borderRadius: 9,
                                                            color: bundleConfig?.bundles_design
                                                                .special_text_background_color,
                                                            background: bundleConfig?.bundles_design.special_text_color,
                                                            padding: "1px 4px",
                                                            fontWeight:
                                                                +bundleConfig?.bundles_design.special_text_style?.split(
                                                                    " ",
                                                                )[0] - 100,
                                                        }}
                                                    >
                                                        {bundleConfig?.y_label_get_text || "GET"}
                                                    </span>

                                                    <span
                                                        style={{
                                                            color: bundleConfig?.bundles_design.special_text_color,
                                                        }}
                                                    >
                                                        {getDiscountValueWithSign(
                                                            bundleConfig?.discount_type,
                                                            bundleConfig?.discount_value || 0,
                                                            currency,
                                                            money_format,
                                                            bundleConfig.y_label_off_text,
                                                            bundleConfig?.free_text,
                                                        )}
                                                    </span>
                                                </div>
                                            </InspectorEditableBlock>
                                        )}

                                    {bundleConfig?.discounted_products?.map((product, index) => {
                                        return (
                                            <>
                                                <ProductCard
                                                    discounted={true}
                                                    key={"2" + product?.id + product?.variants[0]?.id}
                                                    index={bundleConfig?.products?.length + index}
                                                    product={product}
                                                    bundleConfig={bundleConfig}
                                                    appConfigContext={appConfigContext}
                                                    standAlone={standAlone || false}
                                                    setTrackVariant={setTrackDiscountedVariants}
                                                    setUnavailableSelected={setUnavailableDiscountSelected}
                                                    unavailableSelected={unavailableDiscountSelected[index] || []}
                                                    isMobile={isMobile}
                                                    lockToVariants={
                                                        isSameVariantAsBuy
                                                            ? buyVariantsByProduct[product.id]
                                                            : undefined
                                                    }
                                                />
                                                {index < bundleConfig?.discounted_products?.length - 1 && separatorUI}
                                            </>
                                        );
                                    })}
                                </div>
                            </div>
                        </InspectorEditableBlock>
                    </>
                ) : (
                    <>
                        <ProductCardShimmer />
                    </>
                )}
            </div>
        </BundlePreviewWrapper>
    );

    return is_horizontal ? (
        <div
            style={{
                zoom: standAlone ? 1 : 0.85,
                overflow: "hidden",
                width: "100%",
            }}
        >
            {BundlePreviewWrapperUI}
        </div>
    ) : (
        BundlePreviewWrapperUI
    );
};
export default BxgyBundle;
